import React, { useMemo, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import FalconCloseButton from "components/common/FalconCloseButton";
import SubtleBadge from "components/common/SubtleBadge";

import CardDropdown from "components/common/CardDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faEye,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Gear } from "react-bootstrap-icons";
import { handleResetClaimHistoryLog } from "../config/claimSlice";
import {

  ViewClaim,
  ViewClaimResponseHistoryLog,
} from "../config/cliamApi";
import ViewClaimResponseModal from "./ViewClaimResponseModal";
import CancelClaimModal from "./CancelClaimModal";

const ClaimHistoryLogModal = ({
  show,
  setShow,
  row,
  setRow,
  page,
  rowsPerPage,
  setTotal,
  setTotalRows,
  setStatusCounts,
}) => {
  const dispatch = useDispatch();
  const { claimHistoryLogData } = useSelector((state) => state.claim);
  const [showViewResponseModal, setShowViewResponseModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [modalRow, setModalRow] = useState(null);

  const statusConfig = {
    error: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
    Error: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
    invalid: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
    "validation error": {
      bg: "#fff3cd", // Bootstrap warning background
      color: "#856404", // Bootstrap warning text
      icon: "exclamation-triangle",
    },
    "no response": {
      bg: "#08101b",
      color: "#91979e",
      icon: "circle-exclamation",
    },
    approved: { bg: "#d9f8eb", color: "#00894f", icon: "check" },
    "ready to submit": {
      bg: "#d8f3ff",
      color: "#197aa4",
      icon: "check",
    },
    "under progress": {
      bg: "#ffe6cc", // soft orange background
      color: "#cc6600", // deep burnt orange text
      icon: "spinner", // conveys in-progress state
    },
    rejected: { bg: "#fbdbe1", color: "#b82c46", icon: "times" },
    cancelled: { bg: "#ffe0fe", color: "#da23aa", icon: "times" },
    partial: {
      bg: "#C2EEF6",
      color: "#0da9bd",
      icon: "star-half-stroke",
    },
    pended: { bg: "#d9e7fa", color: "#215cac", icon: "clock" },
    "not-required": {
      bg: "#d8f3ff",
      color: "#197aa4",
      icon: "clock",
    },
    queued: { bg: "#fefad2", color: "#a7970d", icon: "list" },
    adjudication: { bg: "#e8dff7", color: "#6f42c1", icon: "gavel" },
    prepared: { bg: "#e6e8ec", color: "#5d6776", icon: "hammer" },
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: ({ row }) => (
          <div
            className="rounded-circle bg-200 d-flex flex-center fs-11"
            style={{ width: 20, height: 20 }}
          >
            {row.index + 1}
          </div>
        ),
      },
      {
        accessorKey: "claimsts",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue()?.toLowerCase();
          const config = statusConfig[status] || {
            bg: "#f8f9fa",
            color: "#000",
          };

          return (
            <SubtleBadge
              pill
              className="d-flex flex-center fs-12 p-2 gap-1 text-capitalize"
              style={{
                backgroundColor: config.bg,
                color: config.color,
                border: `1px solid ${config.color}20`,
                width: "120px",
              }}
            >
              <FontAwesomeIcon
                icon={config.icon}
                transform="shrink-2"
                className="ms-1 me-1"
              />
              {getValue()}
            </SubtleBadge>
          );
        },
      },
      {
        accessorKey: "claim_fullid",
        header: "Claim ID",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "bundle_reqid",
        header: "Request Bundle ID",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        cell: ({ getValue }) =>
          getValue()
            ? moment(getValue()).format("DD-MM-YYYY, hh:mm:ss A")
            : "-",
      },
      {
        accessorKey: "created_by",
        header: "Created By",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "actions",
        header: () => <Gear />,
        enableSorting: false,
        cell: ({ row }) => {
          const status = row?.original?.claimsts?.toLowerCase();
          const claimId = row?.original?.claimid;

          const isAnyCancelled = claimHistoryLogData?.some(
            (item) =>
              item?.claimid === claimId &&
              item?.claimsts?.toLowerCase().trim() === "cancelled"
          );

          const statusConfig = {
            showResponse: [
              "error",
              "approved",
              "pended",
              "queued",
              "rejected",
              "partial",
              "cancelled",
              "noresponse",
              "withdrawn",
            ].includes(status),
            showCancel:
              !isAnyCancelled &&
              ["approved", "pended", "rejected", "partial", ""].includes(
                status
              ),
            showDownloadRequest: status !== "withdrawn",
          };

          const handleViewResponse = async (row) => {
            console.log({ row: row?.original })
            const response = await dispatch(
              ViewClaimResponseHistoryLog({
                claimid: row?.original?.claim_fullid,
                claim_type: row?.original?.claim_type,
                claim_status: row?.original?.claimsts,
                created_by: row?.original?.created_by,
                created_at: row?.original?.created_at
              })
            ).unwrap();

            if (response?.success) {

              setShowViewResponseModal(true);
              setModalRow(row?.original);

            }
          };

          const handleDownloadRequestJson = async (row) => {
            try {
              // const response = await dispatch(
              //   DownloadRequestClaim({
              //     claimid: row?.original?.claim_fullid,
              //     claim_type: row?.original?.claim_type,
              //   })
              // ).unwrap();
              const downloadUrl = row?.original?.claimreq_path;

              if (!downloadUrl) {
                throw new Error("Download URL not found.");
              }

              const fileResponse = await fetch(downloadUrl);
              if (!fileResponse.ok) {
                throw new Error("Failed to fetch the JSON file.");
              }

              const blob = await fileResponse.blob();
              const urlParts = downloadUrl.split("/");
              const filename =
                urlParts[urlParts.length - 1] || "downloaded.json";
              saveAs(blob, filename);
            } catch (error) {
              console.error("Error downloading the JSON file:", error);
            }
          };

          const handleDownloadResponseJson = async (row) => {
            try {
              // const response = await dispatch(
              //   DownloadResponseClaim({
              //     claimid: row?.original?.claim_fullid,
              //     claim_type: row?.original?.claim_type,
              //   })
              // ).unwrap();
              const downloadUrl = row?.original?.claimresp_path;

              if (!downloadUrl) {
                throw new Error("Download URL not found.");
              }

              const fileResponse = await fetch(downloadUrl);
              if (!fileResponse.ok) {
                throw new Error("Failed to fetch the JSON file.");
              }

              const blob = await fileResponse.blob();
              const urlParts = downloadUrl.split("/");
              const filename =
                urlParts[urlParts.length - 1] || "downloaded.json";
              saveAs(blob, filename);
            } catch (error) {
              console.error("Error downloading the JSON file:", error);
            }
          };

          const handleCancelClaim = async (row) => {
            setRow(row?.original);
            setShowCancelModal(true);
          };

          return (
            <CardDropdown icon="ellipsis-v">
              {statusConfig?.showResponse && (
                <Dropdown.Item onClick={() => handleViewResponse(row)}>
                  <FontAwesomeIcon icon={faEye} className="me-2" />
                  View Response
                </Dropdown.Item>
              )}
              {statusConfig?.showCancel && (
                <Dropdown.Item onClick={() => handleCancelClaim(row)}>
                  <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                  Cancel
                </Dropdown.Item>
              )}
              {statusConfig?.showDownloadRequest && (
                <Dropdown.Item onClick={() => handleDownloadRequestJson(row)}>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Download JSON Request
                </Dropdown.Item>
              )}
              {statusConfig?.showResponse && (
                <Dropdown.Item onClick={() => handleDownloadResponseJson(row)}>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Download JSON Response
                </Dropdown.Item>
              )}
            </CardDropdown>
          );
        },
      },
    ],
    [claimHistoryLogData]
  ); // Added claimHistoryLogData to dependencies

  const table = useReactTable({
    data: claimHistoryLogData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <Modal
        show={show}
        centered
        onHide={() => {
          setShow(false);
          dispatch(handleResetClaimHistoryLog());
          setRow(null);
        }}
        dialogClassName="history-log-modal"
        size="lg"
      >
        <Modal.Header className="d-flex justify-content-between align-items-center ps-4 pe-4 pt-3">
          <Modal.Title className="fs-11">Claim History Log</Modal.Title>
          <FalconCloseButton
            size="sm"
            onClick={() => {
              setShow(false);
              dispatch(handleResetClaimHistoryLog());
              setRow(null);
            }}
            className="position-relative"
          />
        </Modal.Header>
        <Modal.Body className="p-4  border">
          <div className="table-responsive">
            <table className="table table-sm fs-10 mb-0">
              <thead className="bg-200 text-dark text-nowrap align-middle">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} className="fw-semibold">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="align-middle">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={table.getAllColumns().length}
                      className="text-center py-3"
                    >
                      No data to display
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>
      <CancelClaimModal
        show={showCancelModal}
        setShow={setShowCancelModal}
        row={row}
        setRow={setRow}
        page={page}
        rowsPerPage={rowsPerPage}
        setTotal={setTotal}
        setTotalRows={setTotalRows}
        setStatusCounts={setStatusCounts}
      />
      <ViewClaimResponseModal
        row={modalRow}
        setShow={setShowViewResponseModal}
        show={showViewResponseModal}
      />
    </>
  );
};

export default ClaimHistoryLogModal;
